<script>
import { mapState } from 'vuex'
import config from '@/config'

const { DEFAULT_CLASSROOM_MENU } = config
const STORAGE_KEY = 'sebraesp:menu-expanded'

export default {
  name: 'VerticalMenu',
  components: {
    Action: () => import('@/components/general/Action'),
    Icon: () => import('@/components/general/Icon'),
    InputField: () => import('@/components/general/InputField'),
    Logo: () => import('@/components/general/Logo'),
    VerticalMenuItem: () => import('@/components/general/VerticalMenuItem'),
    VerticalMenuSubItem: () => import('@/components/general/VerticalMenuSubItem')
  },

  props: {
    items: {
      type: Array,
      default () {
        return []
      }
    },

    classroom: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      expanded: this.readStoredExpanded(),
      icon: 'local_library',
      search: null,
      defaultMenuType: DEFAULT_CLASSROOM_MENU
    }
  },

  computed: {
    ...mapState([
      'Account',
      'Auth'
    ]),

    isSlim () {
      return !this.expanded && this.$media.desktop
    },

    toggleLabel () {
      return this.expanded ? this.$t('global:menu.btn.close') : this.$t('global:menu.btn.open')
    },

    userName () {
      return (this.Auth && this.Auth.userData && this.Auth.userData.name) || null
    },

    userInitials () {
      if (!this.userName) return ''

      const parts = this.userName.trim().split(/\s+/)
      const first = parts[0] ? parts[0][0] : ''
      const last = parts.length > 1 ? parts[parts.length - 1][0] : ''

      return (first + last).toUpperCase()
    },

    bottomItems () {
      const visibleSection = (this.items || []).find(section => {
        return typeof section.deniedProfile === 'undefined' ||
          (this.Account && this.Account.user && this.Account.user.currentProfile !== section.deniedProfile)
      })

      if (!visibleSection || !Array.isArray(visibleSection.items)) return []

      return visibleSection.items.filter(item => item.bottom && item.enabled)
    }
  },

  mounted () {
    this.$emit('resize', this.expanded)
  },

  methods: {
    readStoredExpanded () {
      try {
        const stored = window.localStorage.getItem(STORAGE_KEY)

        if (stored === null) return true

        return stored === '1'
      } catch (e) {
        return true
      }
    },

    persistExpanded () {
      try {
        window.localStorage.setItem(STORAGE_KEY, this.expanded ? '1' : '0')
      } catch (e) {
      }
    },

    expandMenu () {
      this.expanded = !this.expanded
      this.persistExpanded()
      this.$emit('resize', this.expanded)
    },

    closeMenu () {
      this.$emit('close')
    },

    isParentActive (menuItem) {
      return this.$route && this.$route.name ? this.$route.name.startsWith(menuItem.link.name.split('.')[0]) : false
    }
  }
}
</script>

<template>
  <nav
    class="menu"
    :class="{ 'is-reduced': isSlim, 'menu-classroom': classroom }"
    :aria-expanded="String(expanded)"
  >
    <div class="menu-header">
      <Logo
        :theme="$theme"
        :reduced="isSlim"
      />
      <p
        class="menu-company-headline"
        v-html="$t('vertical.menu:company.headline')"
      />
      <Action
        v-if="$media.mobile"
        type="button"
        icon="close"
        @click="closeMenu()"
      />
    </div>
    <div
      v-if="$media.mobile"
      class="menu-section"
    >
      <form class="menu-search-form">
        <InputField
          v-model="search"
          :label="$t('global:menu.search')"
          :floating-label="false"
          append-icon="search"
          dark
        />
      </form>
    </div>
    <template v-for="(section, index1) in items">
      <div
        v-if="typeof section.deniedProfile === 'undefined' || Account.user.currentProfile !== section.deniedProfile"
        :key="index1"
        class="menu-section"
        :class="{'menu-section-first': index1 === 0}"
      >
        <div
          v-if="section.title"
          class="menu-section-title-wrapper"
        >
          <h5 class="menu-section-title">
            {{ expanded ? $t(section.title.expanded) : $t(section.title.reduced) }}
          </h5>
        </div>
        <ul class="menu-list">
          <template
            v-for="(menuItem, index2) in section.items"
          >
            <VerticalMenuItem
              v-if="!menuItem.bottom && menuItem.enabled"
              :key="index2"
              :link="menuItem.link"
              :text="menuItem.text"
              :icon="menuItem.icon"
              :highlight="menuItem.highlight"
              :submenu="Array.isArray(menuItem.items)"
              :role="menuItem.role"
              :disabled="menuItem.disabled"
              :parent-active="isParentActive(menuItem)"
              :exact="menuItem.exact"
              :callback="menuItem.callback"
              :slim="isSlim"
            >
              <VerticalMenuSubItem
                v-for="(menuSubItem, index3) in menuItem.items"
                :key="index3"
                :text="menuSubItem.text"
                :icon="menuSubItem.icon"
              />
            </VerticalMenuItem>
          </template>
        </ul>
      </div>
    </template>
    <div
      v-if="userName && !$media.mobile"
      v-tooltip="isSlim ? { content: userName, placement: 'right', boundariesElement: 'viewport' } : null"
      class="menu-user"
    >
      <span
        class="menu-user-avatar"
        aria-hidden="true"
      >{{ userInitials }}</span>
      <span
        v-if="!isSlim"
        class="menu-user-name"
      >{{ userName }}</span>
    </div>
    <button
      v-if="!$media.mobile"
      v-tooltip="isSlim ? { content: toggleLabel, placement: 'right', boundariesElement: 'viewport' } : null"
      type="button"
      class="menu-toggle"
      :aria-label="toggleLabel"
      :aria-expanded="String(expanded)"
      @click="expandMenu()"
    >
      <Icon
        :name="expanded ? 'keyboard_arrow_left' : 'keyboard_arrow_right'"
        class="menu-toggle-icon"
      />
      <span
        v-if="expanded"
        class="menu-toggle-label"
      >{{ toggleLabel }}</span>
    </button>
    <ul
      v-if="bottomItems.length > 0"
      class="bottom-list"
    >
      <VerticalMenuItem
        v-for="(menuItem, index) in bottomItems"
        :key="`bottom-${index}`"
        :link="menuItem.link"
        :text="menuItem.text"
        :icon="menuItem.icon"
        :highlight="menuItem.highlight"
        :submenu="Array.isArray(menuItem.items)"
        :role="menuItem.role"
        :disabled="menuItem.disabled"
        :exact="menuItem.exact"
        :callback="menuItem.callback"
        :slim="isSlim"
      >
        <VerticalMenuSubItem
          v-for="(menuSubItem, subIndex) in menuItem.items"
          :key="subIndex"
          :text="menuSubItem.text"
          :icon="menuSubItem.icon"
        />
      </VerticalMenuItem>
    </ul>
  </nav>
</template>

<style src="@/assets/styles/themes/default/verticalmenu.css"></style>
